import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import { attendeeDisplayName } from '../utils/attendeeDisplayName';
import { EventSettings } from './events';
import { createPrize, awardPrize } from './prizes';
import { getActiveParticipantIds } from './requests';

export interface LotteryRound {
  id: string;
  eventId: string;
  djId: string;
  roundNumber: number;
  winnerId: string | null;
  winnerNickname: string | null;
  prizeId: string | null;
  participantCount: number;
  drawnAt: string;
  createdAt: string;
}

function docToLotteryRound(docSnap: any): LotteryRound {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    eventId: data.eventId,
    djId: data.djId,
    roundNumber: data.roundNumber,
    winnerId: data.winnerId || null,
    winnerNickname: data.winnerNickname || null,
    prizeId: data.prizeId || null,
    participantCount: data.participantCount,
    drawnAt: data.drawnAt,
    createdAt: data.createdAt,
  };
}

export async function getPastWinnerIds(eventId: string): Promise<Set<string>> {
  const rounds = await getLotteryRounds(eventId);
  const winnerIds = new Set<string>();
  for (const round of rounds) {
    if (round.winnerId) {
      winnerIds.add(round.winnerId);
    }
  }
  return winnerIds;
}

export async function getLotteryRounds(eventId: string): Promise<LotteryRound[]> {
  try {
    const q = query(
      collection(db, 'lotteryRounds'),
      where('eventId', '==', eventId),
      orderBy('drawnAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docToLotteryRound);
  } catch {
    const fallback = await getDocs(
      query(collection(db, 'lotteryRounds'), where('eventId', '==', eventId))
    );
    const rounds = fallback.docs.map(docToLotteryRound);
    rounds.sort((a, b) => b.drawnAt.localeCompare(a.drawnAt));
    return rounds;
  }
}

export function subscribeToDJLotteryRounds(
  eventId: string,
  callback: (rounds: LotteryRound[]) => void
): Unsubscribe {
  const q = query(
    collection(db, 'lotteryRounds'),
    where('eventId', '==', eventId),
    orderBy('drawnAt', 'desc')
  );
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(docToLotteryRound));
  }, (error) => {
    console.warn('[Lottery] Snapshot listener error (index may be building), falling back:', error.message);
    getDocs(query(collection(db, 'lotteryRounds'), where('eventId', '==', eventId)))
      .then((snap) => {
        const rounds = snap.docs.map(docToLotteryRound);
        rounds.sort((a, b) => b.drawnAt.localeCompare(a.drawnAt));
        callback(rounds);
      })
      .catch(() => callback([]));
  });
}

export async function drawWinner(
  eventId: string,
  djId: string,
  settings: EventSettings
): Promise<LotteryRound> {
  console.log('[Lottery] Step 1: Getting participants...');
  const participants = await getActiveParticipantIds(eventId);
  console.log(`[Lottery] Found ${participants.length} participants`);

  let eligible = participants;
  if (!settings.lotteryAllowRepeatWinners) {
    console.log('[Lottery] Step 2: Filtering repeat winners...');
    const pastWinners = await getPastWinnerIds(eventId);
    eligible = participants.filter((p) => !pastWinners.has(p.id));
    console.log(`[Lottery] ${eligible.length} eligible after filtering`);
  }

  if (eligible.length === 0) {
    throw new Error('No eligible participants for lottery draw');
  }

  const randomIndex =
    crypto.getRandomValues(new Uint32Array(1))[0] % eligible.length;
  const winner = eligible[randomIndex];
  console.log(`[Lottery] Step 3: Selected winner index ${randomIndex}`);

  const existingRounds = await getLotteryRounds(eventId);
  const roundNumber = existingRounds.length + 1;
  console.log(`[Lottery] Round number: ${roundNumber}`);

  const prizeType = settings.lotteryPrizeType || 'shoutout';
  const now = new Date().toISOString();

  console.log('[Lottery] Step 4: Creating prize...');
  const prizeId = await createPrize(eventId, djId, prizeType, {
    customText: settings.lotteryPrizeCustomText,
    timeoutMinutes: settings.prizeTimeoutMinutes ?? 5,
    gameSource: 'lottery',
    gameRoundId: `lottery-${roundNumber}`,
  });
  console.log(`[Lottery] Prize created: ${prizeId}`);

  console.log('[Lottery] Step 5: Awarding prize...');
  const winnerName = attendeeDisplayName(winner.nickname, winner.id);
  await awardPrize(prizeId, winner.id, winnerName);

  const roundData = {
    eventId,
    djId,
    roundNumber,
    winnerId: winner.id,
    winnerNickname: winnerName,
    prizeId,
    participantCount: participants.length,
    drawnAt: now,
    createdAt: now,
  };

  console.log('[Lottery] Step 6: Saving round...');
  const docRef = await addDoc(collection(db, 'lotteryRounds'), roundData);
  console.log(`[Lottery] Round saved: ${docRef.id}`);

  return {
    id: docRef.id,
    ...roundData,
  };
}
